import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError } from "@/lib/adminApi";
import {
  ARCHIVE_ENDPOINT,
  COMPLETED_CONFIRM_PHRASE,
  MAX_ARCHIVE_REASON_LENGTH,
  PRESET_REASONS,
  RESULT_MESSAGES,
  buildArchiveRequestBody,
  composeArchiveReason,
  isArchivedSuccess,
  isSubmitReady,
  maxNoteLength,
} from "@/lib/archiveForm";
import { GhostButton, PrimaryButton, StatusBadge, shortDate } from "@/components/admin/uiKit";

/**
 * R6B — Archive job dialog. Reused by AdminJobs row and JobDrawer.
 *
 * Props:
 *   job:          the job being archived. MUST include id, status,
 *                 scheduled_for, customer_name / customer_email.
 *   onClose():    called on Cancel and on ARCHIVED success.
 *   onArchived(): called only on ARCHIVED success — the caller
 *                 refetches the currently mounted list.
 */
export default function ArchiveJobDialog({ job, onClose, onArchived }) {
  // Capture the IMMUTABLE job id at open time. A background list
  // refresh cannot retarget this dialog to a different row.
  const jobIdRef = useRef(job?.id);
  useEffect(() => {
    jobIdRef.current = job?.id;
  }, [job?.id]);

  const requireCompletedGate = job?.status === "completed";

  const [preset, setPreset] = useState(PRESET_REASONS[0]);
  const [note, setNote] = useState("");
  const [completedInput, setCompletedInput] = useState("");
  const [inFlight, setInFlight] = useState(false);

  // Reset every field when the dialog opens for a different job.
  useEffect(() => {
    setPreset(PRESET_REASONS[0]);
    setNote("");
    setCompletedInput("");
    setInFlight(false);
  }, [job?.id]);

  const composed = useMemo(() => composeArchiveReason(preset, note), [preset, note]);
  const maxNote = useMemo(() => maxNoteLength(preset), [preset]);
  const submitReady = isSubmitReady({
    preset, note, requireCompletedGate, completedInput, inFlight,
  });

  const onSubmit = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    // Double-submit prevention. isSubmitReady already checks inFlight,
    // but we guard again here in case an event fires between renders.
    if (!submitReady || inFlight) return;
    setInFlight(true);
    const body = buildArchiveRequestBody({
      jobId: jobIdRef.current,
      preset,
      note,
    });
    try {
      const { data } = await adminApi.post(ARCHIVE_ENDPOINT, body);
      const code = data?.result;
      if (isArchivedSuccess(data)) {
        toast.success(RESULT_MESSAGES.ARCHIVED);
        onArchived?.();
        onClose?.();
        return;
      }
      // Any non-ARCHIVED result: NO list/cache mutation, NO close.
      const message = RESULT_MESSAGES[code] || `Unexpected result: ${code || "unknown"}`;
      toast.error(message);
      setInFlight(false);
    } catch (err) {
      // Non-2xx branch: 401 handled by existing auth flow (adminApi
      // token expiry / clearAdminToken pattern); 404 (JOB_NOT_FOUND),
      // 422 (schema violation), network / other errors — display and
      // stay open. No mutation.
      const status = err?.response?.status;
      let message;
      if (status === 404) {
        message = "The job no longer exists; refresh.";
      } else if (status === 401) {
        message = formatAdminError(err, "Not authenticated — please sign in again.");
      } else if (status === 422) {
        message = formatAdminError(err, "Request rejected.");
      } else if (!err?.response) {
        message = err?.message || "Network error — try again.";
      } else {
        message = formatAdminError(err, "Archive failed.");
      }
      toast.error(message);
      setInFlight(false);
    }
  };

  if (!job) return null;

  const composedOverLimit = composed.length > MAX_ARCHIVE_REASON_LENGTH;

  return (
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="archive-dialog-title"
      data-testid="archive-job-dialog"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => (inFlight ? null : onClose?.())}
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
      />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white shadow-2xl rounded-xl overflow-hidden">
        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">
              Archive
            </p>
            <h2
              id="archive-dialog-title"
              className="text-lg font-extrabold tracking-tight text-slate-900"
              style={{ fontFamily: "Outfit, ui-sans-serif" }}
            >
              Archive this job?
            </h2>
          </div>
          <button
            type="button"
            onClick={() => (inFlight ? null : onClose?.())}
            className="h-8 w-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 grid place-items-center"
            data-testid="archive-dialog-close"
            disabled={inFlight}
          >
            <X size={14} />
          </button>
        </header>

        <form onSubmit={onSubmit} className="p-5 space-y-5">
          {/* Read-only job identifying details. */}
          <div
            className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm"
            data-testid="archive-dialog-job-summary"
          >
            <p className="font-semibold text-slate-900">
              {job.customer_name || job.customer_email}
            </p>
            <div className="mt-1 flex items-center gap-2 text-slate-600">
              <span>{shortDate(job.scheduled_for)}</span>
              <span aria-hidden>·</span>
              <StatusBadge status={job.status} />
            </div>
          </div>

          {/* Preset. */}
          <div>
            <label
              htmlFor="archive-preset"
              className="block text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-1.5"
            >
              Reason
            </label>
            <select
              id="archive-preset"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              data-testid="archive-preset-select"
              disabled={inFlight}
            >
              {PRESET_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* Optional note. Required when preset is Other. */}
          <div>
            <label
              htmlFor="archive-note"
              className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-1.5"
            >
              <span>
                {preset === "Other" ? "Note (required)" : "Note (optional)"}
              </span>
              <span
                className={`text-[10px] font-mono ${composedOverLimit ? "text-rose-600" : "text-slate-400"}`}
                data-testid="archive-composed-count"
              >
                {composed.length} / {MAX_ARCHIVE_REASON_LENGTH}
              </span>
            </label>
            <textarea
              id="archive-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={maxNote}
              placeholder={
                preset === "Other"
                  ? "Describe the reason. Required when 'Other' is selected."
                  : "Optional. Appended to the reason as \"Preset: Note\"."
              }
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              data-testid="archive-note-textarea"
              disabled={inFlight}
            />
            {composedOverLimit && (
              <p className="mt-1 text-xs text-rose-600" data-testid="archive-over-limit">
                The composed reason exceeds {MAX_ARCHIVE_REASON_LENGTH} characters. Shorten your note.
              </p>
            )}
          </div>

          {/* Completed-job protection: additional typed gate. */}
          {requireCompletedGate && (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
              data-testid="archive-completed-gate"
            >
              <label
                htmlFor="archive-completed-input"
                className="block text-[10px] uppercase tracking-[0.22em] text-amber-800 font-bold mb-1.5"
              >
                This job is marked COMPLETED
              </label>
              <p className="text-xs text-amber-900 mb-2">
                Archiving a completed job erases a claim that work was done. Type{" "}
                <code className="bg-white/70 px-1 rounded font-mono">
                  {COMPLETED_CONFIRM_PHRASE}
                </code>{" "}
                (case-sensitive) to enable Archive.
              </p>
              <input
                id="archive-completed-input"
                type="text"
                value={completedInput}
                onChange={(e) => setCompletedInput(e.target.value)}
                autoComplete="off"
                placeholder="Type COMPLETED here"
                className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                data-testid="archive-completed-input"
                disabled={inFlight}
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <GhostButton
              type="button"
              onClick={() => (inFlight ? null : onClose?.())}
              disabled={inFlight}
              data-testid="archive-cancel"
            >
              Cancel
            </GhostButton>
            <PrimaryButton
              type="submit"
              disabled={!submitReady}
              data-testid="archive-submit"
            >
              {inFlight ? "Archiving…" : "Archive job"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}
